import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AtsSkeleton, Select, Table, Typography, TYPOGRAPHY_WEIGHT } from '_atoms';
import { ReactComponent as EmptyImg } from 'dist/emptyStates/stepsNoCandidates.svg';
import { SELECT_TYPES } from '_atoms/Select/constants';
import { PROMOTION_REQUEST_STATUS_OPTIONS } from 'constants/promotionRequest';
import type { SelectOption } from '_atoms/Select/types';
import type { PaginatedResponse } from 'store/reducers/types';
import type { PromotionRequestListItem } from 'actions';
import { COLUMNS, INITIAL_TABLE_VALUES } from './constants';
import { dataMapper, paramsMapper } from './utils';
import type { PromotionRequestRow, TableSorting } from './utils';
import './style.scss';

type LimitOption = { value: number; label: number };

type FetchRequests = (
  params: Record<string, unknown>
) => Promise<PaginatedResponse<PromotionRequestListItem> | null | undefined>;

interface PromotionRequestsTableProps {
  fetchRequests: FetchRequests;
  cardTitle: string;
  extra?: React.ReactNode;
  emptyComponent?: React.ReactElement;
}

export const TableEmpty = () => {
  const { t } = useTranslation();
  return (
    <div className="promotion-requests-table__empty">
      <EmptyImg className="empty-image" />
      <Typography.Title3 weight={TYPOGRAPHY_WEIGHT.BOLD} className="promotion-requests-table__empty-title">
        {t('translation:job_promotion_requests_tab.table_empty_title')}
      </Typography.Title3>
      <Typography.Text className="promotion-requests-table__empty-subtitle">
        {t('translation:job_promotion_requests_tab.table_empty_subtitle')}
      </Typography.Text>
    </div>
  );
};

export const PromotionRequestsTable = ({
  fetchRequests,
  cardTitle,
  extra,
  emptyComponent,
}: PromotionRequestsTableProps) => {
  const { t } = useTranslation();
  const [tableOptions, setTableOptions] = useState(INITIAL_TABLE_VALUES);
  const [requestsList, setRequestsList] = useState<PromotionRequestRow[]>([]);
  const [statuses, setStatuses] = useState<SelectOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);

  const handleChangeDate = (field: string, item: number | TableSorting | LimitOption) => {
    setTableOptions(s => ({ ...s, [field]: item }));
    if (field === 'page') {
      setTableOptions(s => ({ ...s, offset: ((item as number) - 1) * s.limit.value }));
    } else if (field === 'limit') {
      setTableOptions(s => ({ ...s, offset: 0, page: 1 }));
    }
  };

  const setSorting = (sort: TableSorting) => handleChangeDate('sorting', sort);

  const handleStatusesChange = (selectedStatuses: SelectOption[]) => {
    setStatuses(selectedStatuses);
    setTableOptions(s => ({ ...s, offset: 0, page: 1 }));
  };

  const columns = COLUMNS(tableOptions.sorting, setSorting);

  const getRequests = () => {
    setLoading(true);
    fetchRequests(paramsMapper(tableOptions, statuses))
      .then(resp => {
        setRequestsList(dataMapper(resp?.results));
        setTableOptions(s => ({ ...s, itemsCount: resp?.count ?? 0 }));
      })
      .catch(() => {
        setRequestsList([]);
        setTableOptions(s => ({ ...s, itemsCount: 0 }));
      })
      .finally(() => {
        setLoading(false);
        setInitialLoading(false);
      });
  };

  useEffect(getRequests, [tableOptions.sorting, tableOptions.limit, tableOptions.offset, tableOptions.page, statuses]);

  const extraContent = (
    <div className="promotion-requests-table__extra">
      <Select
        className="promotion-requests-table__status"
        placeholder={t('translation:job_promotion_requests_tab.ph_select_status')}
        options={PROMOTION_REQUEST_STATUS_OPTIONS}
        mode={SELECT_TYPES.MULTIPLE}
        value={statuses}
        onChange={handleStatusesChange}
      />
      {extra}
    </div>
  );

  if (initialLoading) {
    return <AtsSkeleton height={456} />;
  }

  if (!requestsList.length && !statuses.length && emptyComponent) {
    return emptyComponent;
  }

  return (
    <div className="promotion-requests-table">
      <Table
        rowKey="uuid"
        className="promotion-requests-table__table"
        cardTitle={cardTitle}
        cardCount={tableOptions.itemsCount}
        extra={extraContent}
        columns={columns}
        loading={loading}
        data={requestsList}
        limit={tableOptions.limit}
        page={tableOptions.page}
        itemsCount={tableOptions.itemsCount}
        updateParams={handleChangeDate}
        showCardFooter={!!requestsList.length}
        defaultTableMobile
        emptyComponent={<TableEmpty />}
      />
    </div>
  );
};
